import { Button } from "@/components/ui/Button";
import { Tooltip } from "@/components/ui/Tooltip";
import { useMeetingState, useTranscription } from "@daily-co/daily-react";
import { Circle } from "lucide-react";

/**
 * Compact recording indicator shown in the call Tray. A blinking bright-red dot
 * signals the visit is being transcribed *right now*; a solid black dot means
 * transcription (and the AI features it feeds) is off for this visit. The full
 * status text is exposed via the tooltip / accessible name.
 *
 * Driven by the live Daily transcription state (`useTranscription`), NOT the
 * per-encounter consent bool. Consent persists for the encounter, but a single
 * bool can read "consented" on a re-started call where transcription never
 * actually started — showing a recording dot while no `.vtt` is produced. Keying
 * the dot off the real transcription state makes it honest in both directions.
 *
 * Renders only while the meeting is actively joined. During call teardown
 * (clinician hits "leave") transcription stops a beat before the window closes;
 * gating on the joined state avoids flashing a "transcription off" black dot in
 * those final seconds, which would read as a confusing state change.
 */
export const RecordingConsentIndicator = () => {
  const meetingState = useMeetingState();
  const { isTranscribing } = useTranscription();

  if (meetingState !== "joined-meeting") {
    return null;
  }

  const label = isTranscribing
    ? "This visit is being transcribed. Transcription + AI Supported Features are active."
    : "This visit is not being transcribed. Transcription + AI Supported Features are off for this visit.";

  return (
    <Tooltip tooltipContentText={label} side="top">
      <Button
        variant="secondary"
        size="icon"
        className="h-6 w-6 cursor-default"
        aria-label={label}
      >
        {isTranscribing ? (
          <Circle size={12} className="animate-blink fill-error stroke-error" />
        ) : (
          <Circle size={12} className="fill-black stroke-black" />
        )}
      </Button>
    </Tooltip>
  );
};
